// Slot-machine "roll" text animation (à la slot-text), but built on the RN
// Reanimated the repo already ships — so it works on mobile-web *and* native,
// matches the shared <Text> styling, and adds no new dependency.
//
// Each character lives in its own fixed-height, overflow-hidden cell. When a
// character changes, the cell renders [prevChar, nextChar] stacked vertically
// and slides up by one cell height, with a per-character stagger so the digits
// settle left-to-right like a flip-board / slot reel.
import React, { useEffect, useState } from 'react'
import { View } from 'react-native'
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming
} from 'react-native-reanimated'

import Text, { Props as TextProps } from '@common/components/Text'

export interface RollingTextProps extends Omit<TextProps, 'children'> {
  /** The text to display. Changes animate; the first value mounts statically. */
  value: string | number
  /** Cell height = fontSize * ratio. Bump if tall glyphs clip. */
  lineHeightRatio?: number
  /** Per-character stagger in ms (left-to-right cascade). */
  stagger?: number
  /** Roll duration per character in ms. */
  duration?: number
}

// Narrow glyphs (period, comma…) have a font advance far wider than their ink,
// so an isolated cell leaves a visible gap before the next character. We pull
// the following cell left by a fraction of fontSize to close that dead space —
// no clipping/centering, the glyph itself stays at its natural position.
const NARROW_CHAR_PULL: { [c: string]: number } = {
  '.': 0.025,
  ',': 0.025,
  "'": 0.02,
  ':': 0.015
}

const RollChar: React.FC<{
  char: string
  index: number
  height: number
  pullRight?: number
  stagger: number
  duration: number
  textProps: Omit<TextProps, 'children'>
}> = ({ char, index, height, pullRight, stagger, duration, textProps }) => {
  // `prev` is what's currently shown, `next` is what we roll to.
  const [pair, setPair] = useState({ prev: char, next: char })
  const progress = useSharedValue(0)

  useEffect(() => {
    if (char === pair.next) return
    setPair({ prev: pair.next, next: char })
    // Reset instantly (invisible because the pair swaps in the same commit),
    // then roll up to reveal the incoming character.
    progress.value = 0
    progress.value = withDelay(
      index * stagger,
      withTiming(1, { duration, easing: Easing.out(Easing.cubic) })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [char])

  const animStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: -progress.value * height }]
  }))

  // Force each glyph into one cell-height line so the stack aligns exactly.
  const cellStyle = [textProps.style, { height, lineHeight: height }] as any

  return (
    <View style={{ height, marginRight: pullRight, overflow: 'hidden' }}>
      <Animated.View style={animStyle}>
        <Text {...textProps} style={cellStyle}>
          {pair.prev === ' ' ? ' ' : pair.prev}
        </Text>
        <Text {...textProps} style={cellStyle}>
          {pair.next === ' ' ? ' ' : pair.next}
        </Text>
      </Animated.View>
    </View>
  )
}

// Narrow punctuation ('.', ',', …) is rendered statically rather than rolled.
// A rolling cell stacks [prevGlyph, nextGlyph] and its width is the wider of the
// two, so a digit→'.' transition would inflate the '.' cell to a full digit
// width for the roll's duration (a visible stretch + dead gap). Punctuation
// reads identically whether it rolls or not, so we skip the animation and keep
// the cell at its true glyph width at all times. Remounting (RollChar↔StaticChar
// when an index flips between a digit and punctuation) resets state cleanly.
const StaticChar: React.FC<{
  char: string
  height: number
  pullRight?: number
  textProps: Omit<TextProps, 'children'>
}> = ({ char, height, pullRight, textProps }) => (
  <View style={{ height, marginRight: pullRight, overflow: 'hidden', justifyContent: 'flex-end' }}>
    <Text {...textProps} style={[textProps.style, { height, lineHeight: height }] as any}>
      {char}
    </Text>
  </View>
)

// Every rolled character costs a wrapper View, two <Text> nodes, a Reanimated
// shared value and an animated style. That is fine for a balance ("1,234.56")
// and pathological for a max-uint256 meme-token amount, which arrives here as a
// 70–100 character string — a single row would then mount hundreds of animated
// cells. Past this length the roll conveys nothing anyway (no one reads a
// slot-machine cascade of 80 digits), so we render the value statically and skip
// the per-character machinery entirely. Callers that compact their values
// (see `compactHugeAmount`) never hit this path.
const MAX_ROLL_CHARS = 16

const RollingText: React.FC<RollingTextProps> = ({
  value,
  lineHeightRatio = 1.25,
  stagger = 35,
  duration = 350,
  fontSize = 14,
  style,
  ...rest
}) => {
  const height = Math.ceil(fontSize * lineHeightRatio)
  const chars = String(value).split('')
  // Styling handed to every inner <Text> so cells render identically.
  const textProps = { ...rest, fontSize, style }

  if (chars.length > MAX_ROLL_CHARS) {
    return (
      <Text {...textProps} numberOfLines={1}>
        {String(value)}
      </Text>
    )
  }

  return (
    <View style={{ flexDirection: 'row', alignItems: 'flex-end', height }}>
      {chars.map((c, i) => {
        const pull = NARROW_CHAR_PULL[c]
        // Clamp to at least 1px: at small fontSizes Math.round(fontSize * pull)
        // floors to 0 (e.g. 14 * 0.025 = 0.35 → 0), silently dropping the fix
        // for narrow glyphs in compact rows (asset list balances/USD).
        const pullRight = pull ? -Math.max(1, Math.round(fontSize * pull)) : undefined
        // Punctuation renders statically (no roll) so its cell never inflates to
        // a digit width during a transition; digits still roll.
        return pull ? (
          // eslint-disable-next-line react/no-array-index-key
          <StaticChar key={i} char={c} height={height} pullRight={pullRight} textProps={textProps} />
        ) : (
          <RollChar
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            char={c}
            index={i}
            height={height}
            pullRight={pullRight}
            stagger={stagger}
            duration={duration}
            textProps={textProps}
          />
        )
      })}
    </View>
  )
}

export default React.memo(RollingText)
